import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  BookOpen,
  Building2,
  CalendarCheck,
  Camera,
  CheckSquare,
  ClipboardList,
  Eye,
  FileQuestion,
  FileText,
  FolderOpen,
  Images,
  Layers,
  LayoutDashboard,
  MapPin,
  ScanLine,
  ShieldAlert,
  Settings,
  Users,
  Video,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { useFeatureAccess } from '@/shared/hooks/useFeatureAccess';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';
import { useProjectSummary } from '../hooks/useProjectSummary';
import type { ProjectFeatureSummary, RecentActivityItem } from '../types';

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  'check-square': <CheckSquare className="h-4 w-4" />,
  eye: <Eye className="h-4 w-4" />,
  'file-question': <FileQuestion className="h-4 w-4" />,
  'clipboard-list': <ClipboardList className="h-4 w-4" />,
  'shield-alert': <ShieldAlert className="h-4 w-4" />,
  scan: <ScanLine className="h-4 w-4" />,
};

const FEATURE_ICONS: Record<string, React.ReactNode> = {
  project_dashboard: <LayoutDashboard />,
  progress: <Activity />,
  asbuilds: <BookOpen />,
  drones: <Layers />,
  drone_photos: <Camera />,
  surveillance: <Video />,
  photo_gallery: <Images />,
  drawings: <FileText />,
  punch_lists: <CheckSquare />,
  observations: <Eye />,
  safety: <ShieldAlert />,
  rfis: <FileQuestion />,
  submittals: <ClipboardList />,
  files: <FolderOpen />,
  daily_log: <CalendarCheck />,
  team: <Users />,
  meetings: <Video />,
};

const FEATURE_GROUPS = [
  {
    label: 'General',
    keys: ['project_dashboard'],
  },
  {
    label: 'Ironview 360',
    keys: ['progress', 'asbuilds', 'drones', 'drone_photos', 'surveillance', 'photo_gallery'],
  },
  {
    label: 'Construction Management',
    keys: ['drawings', 'punch_lists', 'observations', 'safety', 'rfis', 'submittals', 'files', 'daily_log'],
  },
  {
    label: 'Personnel Management',
    keys: ['team', 'meetings'],
  },
] as const;

const statusTone = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized.includes('closed') || normalized.includes('completed') || normalized.includes('approved')) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300';
  }
  if (normalized.includes('overdue') || normalized.includes('rejected')) {
    return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300';
  }
  if (normalized.includes('review') || normalized.includes('pending') || normalized.includes('revise')) {
    return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300';
  }
  return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300';
};

function ActionChip({
  href,
  isActive,
  children,
  external = false,
  icon,
  onNavigate,
}: {
  href?: string;
  isActive: boolean;
  children: React.ReactNode;
  external?: boolean;
  icon?: React.ReactNode;
  onNavigate: (href: string) => void;
}) {
  const chipClass = `inline-flex items-center gap-2.5 rounded-[11px] border border-border bg-muted px-3.5 py-2.5 text-[13.5px] font-semibold text-foreground transition-colors duration-150 ${
    isActive
      ? 'hover:border-primary hover:bg-primary/12 hover:ring-[3px] hover:ring-primary/15 hover:[&_svg]:text-primary'
      : 'pointer-events-none cursor-not-allowed opacity-40 grayscale'
  }`;
  const chipContent = (
    <>
      {icon && (
        <span className="flex h-4 w-4 items-center text-muted-foreground transition-colors [&_svg]:h-4 [&_svg]:w-4 [&_svg]:stroke-[1.9]">
          {icon}
        </span>
      )}
      {children}
    </>
  );

  if (!isActive || !href) {
    return <span className={chipClass}>{chipContent}</span>;
  }

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={chipClass}>
        {chipContent}
      </a>
    );
  }

  return (
    <button type="button" onClick={() => onNavigate(href)} className={chipClass}>
      {chipContent}
    </button>
  );
}

function ProjectCard({
  project,
  features,
  featureFlagByKey,
  canManageProject,
  onNavigate,
}: {
  project: NonNullable<ReturnType<typeof useProjectSummary>['data']>['project'];
  features: ProjectFeatureSummary[];
  featureFlagByKey: Record<string, boolean>;
  canManageProject: boolean;
  onNavigate: (href: string) => void;
}) {
  const [thumbnailError, setThumbnailError] = useState(false);
  const clientName = project.client?.name ?? '';
  const thumbnailSrc = project.thumbnail || project.thumbnail_url || '';
  const featureByKey = new Map(features.map((feature) => [feature.key, feature]));
  // Features the summary endpoint doesn't report, mirroring the chips on the
  // main dashboard's project card (pages/Dashboard.tsx).
  featureByKey.set('project_dashboard', {
    key: 'project_dashboard',
    label: 'Project Dashboard',
    enabled: true,
    href: `/app/property/${clientName}/${project.id}/dashboard`,
  });
  const settingsHref = clientName
    ? `/app/property/${clientName}/${project.id}/settings`
    : `/app/property/${project.id}/settings`;

  return (
    <Card className="relative grid overflow-hidden md:grid-cols-[0.92fr_1.08fr]">
      {canManageProject && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="absolute right-4 top-4 z-10 size-10 border-border bg-background/85 text-muted-foreground shadow-sm backdrop-blur-md hover:text-primary"
          aria-label={`Open settings for ${project.name}`}
          title="Project settings"
          onClick={() => onNavigate(settingsHref)}
        >
          <Settings className="size-4" strokeWidth={1.9} />
        </Button>
      )}
      <div className="relative min-h-[230px] overflow-hidden bg-[#10131b] md:min-h-[430px]">
        {thumbnailSrc && !thumbnailError ? (
          <a
            href={thumbnailSrc}
            data-lightbox={`${project.name}-box`}
            data-title={project.name}
            className="block h-full w-full"
          >
            <img
              src={thumbnailSrc}
              alt={`${project.name} thumbnail`}
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => setThumbnailError(true)}
            />
          </a>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Building2 className="h-12 w-12 text-muted-foreground/60" strokeWidth={1.9} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[55%] to-card md:bg-gradient-to-r" />
      </div>

      <div className="px-[34px] pb-[34px] pt-[30px]">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-[1.875rem] font-extrabold tracking-[-0.02em]">
            {project.name}
          </h2>
          <Badge variant="outline">{project.is_active ? 'Active' : 'Completed'}</Badge>
        </div>

        {project.address && project.address !== 'None' && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(project.address)}`}
            className="my-[11px] inline-flex max-w-full items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <MapPin className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.9} />
            <span className="truncate">{project.address}</span>
          </a>
        )}

        {FEATURE_GROUPS.map((group) => (
          <div key={group.label} className="mt-[22px]">
            <h3 className="mb-[11px] flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
              <span>{group.label}</span>
              <span className="h-px flex-1 bg-border" />
            </h3>
            <div className="flex flex-wrap gap-2.5">
              {group.keys.map((key) => {
                const feature = featureByKey.get(key);
                if (!feature) return null;
                // Backend `enabled` carries availability + the user's RBAC
                // permission; the frontend feature flags mirror the gating
                // on the main dashboard's project card.
                const isActive = feature.enabled && (featureFlagByKey[feature.key] ?? true);
                return (
                  <ActionChip
                    key={feature.key}
                    href={feature.href}
                    isActive={isActive}
                    external={feature.key === 'drone_photos'}
                    icon={FEATURE_ICONS[feature.key]}
                    onNavigate={onNavigate}
                  >
                    {feature.label}
                  </ActionChip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RecentActivity({ items, onNavigate }: { items: RecentActivityItem[]; onNavigate: (href: string) => void }) {
  return (
    <Card className="border-border/80 shadow-sm xl:sticky xl:top-20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No recent activity yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <button
                key={`${item.type}-${item.href}-${item.updated_at}`}
                type="button"
                className="flex w-full items-start gap-3 rounded-md border border-transparent p-2 text-left transition-colors hover:border-border hover:bg-muted/50"
                onClick={() => onNavigate(item.href)}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                  {ACTIVITY_ICONS[item.icon] ?? <Activity className="h-4 w-4" />}
                </span>
                <span className="min-w-0 flex-1 overflow-hidden">
                  <span className="line-clamp-2 break-words text-sm font-medium leading-5 text-foreground">
                    {item.title}
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{item.type}</span>
                    {item.status && (
                      <Badge variant="outline" className={cn('rounded-md px-1.5 py-0 text-[10px]', statusTone(item.status))}>
                        {item.status.replace(/_/g, ' ')}
                      </Badge>
                    )}
                    {item.updated_at && (
                      <span>{formatDistanceToNow(parseISO(item.updated_at), { addSuffix: true })}</span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <BaseLayout>
      <div className="mx-auto grid w-full max-w-[1500px] gap-5 px-4 py-8 sm:px-6 lg:px-9 xl:grid-cols-[minmax(0,1fr)_23rem]">
        <Skeleton className="h-[430px] rounded-[1.375rem]" />
        <Skeleton className="h-[430px] rounded-[1.375rem]" />
      </div>
    </BaseLayout>
  );
}

export const ProjectDashboardPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const numericProjectId = projectId ? Number(projectId) : undefined;
  const { data, isLoading, error } = useProjectSummary(numericProjectId);

  // The same frontend feature flags the main dashboard's project card
  // applies (pages/Dashboard.tsx); keys match the summary feature keys.
  const photoGalleryAccess = useFeatureAccess(FEATURE_FLAGS.PHOTO_GALLERY);
  const drawingsAccess = useFeatureAccess(FEATURE_FLAGS.DRAWINGS);
  const punchListsAccess = useFeatureAccess(FEATURE_FLAGS.PUNCH_LISTS);
  const observationsAccess = useFeatureAccess(FEATURE_FLAGS.OBSERVATIONS);
  const safetyAccess = useFeatureAccess(FEATURE_FLAGS.SAFETY);
  const rfisAccess = useFeatureAccess(FEATURE_FLAGS.RFIS);
  const submittalsAccess = useFeatureAccess(FEATURE_FLAGS.SUBMITTALS);
  const filesAccess = useFeatureAccess(FEATURE_FLAGS.FILES);
  const dailyLogAccess = useFeatureAccess(FEATURE_FLAGS.DAILY_LOG);
  const meetingMinutesAccess = useFeatureAccess(FEATURE_FLAGS.MEETING_MINUTES);
  const featureFlagByKey: Record<string, boolean> = {
    photo_gallery: photoGalleryAccess.isEnabled,
    drawings: drawingsAccess.isEnabled,
    punch_lists: punchListsAccess.isEnabled,
    observations: observationsAccess.isEnabled,
    safety: safetyAccess.isEnabled,
    rfis: rfisAccess.isEnabled,
    submittals: submittalsAccess.isEnabled,
    files: filesAccess.isEnabled,
    daily_log: dailyLogAccess.isEnabled,
    meetings: meetingMinutesAccess.isEnabled,
  };

  if (isLoading) return <DashboardSkeleton />;

  if (error || !data) {
    return (
      <BaseLayout>
        <div className="flex min-h-[calc(var(--app-vh,1vh)*100-4rem)] items-center justify-center p-6">
          <Card className="max-w-md border-border/80">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
              <p className="mt-3 text-sm text-muted-foreground">
                Project summary could not be loaded.
              </p>
            </CardContent>
          </Card>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto grid w-full max-w-[1500px] gap-5 px-4 py-8 sm:px-6 lg:px-9 xl:grid-cols-[minmax(0,1fr)_23rem]">
        <ProjectCard
          project={data.project}
          features={data.features}
          featureFlagByKey={featureFlagByKey}
          canManageProject={data.can_manage_rbac}
          onNavigate={navigate}
        />
        <RecentActivity items={data.recent_activity} onNavigate={navigate} />
      </div>
    </BaseLayout>
  );
};
